import React, { useMemo } from "react";
import { Box, Typography, Button } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { ChatbotIcon } from "../../ChatbotIcon";
import AIPrescriptionFetchingProcess from "../AIPrescriptionFetchingProcess";
import PrescriptionSummary from "../PrescriptionSummary";
import FeedbackButtons from "../FeedbackButtons";
import CopyButton from "../CopyButton";
import PharmacyList from "../PharmacyList";
import Suggestions from "../Suggestions";
import PharmacyLookup from "../PharmacyLookup";
import PbmLookup from "../PbmLookup";
import FavouriteDrugsLookup from "../FavouriteDrugsLookup";
import ScheduledMfaLivePanel from "../ScheduledMfaLivePanel";
import {
  InteractionsSummaryCard,
} from "../Interactions";
import { drugInteractionResponseToInteractionItems } from "../Interactions/drugInteractionUtils";
import { AllergySummaryCard } from "../Allergies";
import type { AllergyResponse } from "../Allergies/types";
import type { DrugInteractionResponse } from "../Interactions/types";
import { isHighSeverity } from "../Interactions/severityUtils";
import { INTENT_HIGH_SEVERITY, INTENT_VIEW_INTERACTIONS } from "../Interactions/constants";
import { capitalizeFirstLetter } from "../../../utils/textUtils";
import { getPharmaciesFromPharmacyLookup } from "../../../utils/AIPrescription/pharmacyUtils";
import {
  filterPrescriptionsForSummaryDisplay,
  isMultiRxPrescriptionDraft,
  prescriptionsForLockedMedications,
  pharmacySuggestionGroupsFromPayload,
  shouldDeferPharmacyForMedicationSuggestions,
} from "../../../utils/AIPrescription/prescriptionUtils";
import { picksForConversation } from "../../../utils/AIPrescription/formUtils";
import type { ConversationItem, IntentRecord } from "../../../utils/AIPrescription/types";
import type { InteractionItem } from "../Interactions/types";
import {
  isAllowedAppTabPath,
  normalizeAppTabLinks,
} from "../../../utils/AIPrescription/appTabLink";

function firstTokenCore(name: string): string {
  const t = name.trim().split(/\s+/)[0] ?? "";
  return t.replace(/[^a-zA-Z0-9-]/g, "").toLowerCase();
}

/** When orchestration names the Rx line with the high-severity issue, keep only matching high rows. */
function filterHighInteractionsForPrimaryMedication(
  items: InteractionItem[],
  primaryMedicationName: string
): InteractionItem[] {
  const display = primaryMedicationName.trim();
  if (!display) return items;
  const core = firstTokenCore(display);
  if (!core) return items;
  const highs = items.filter((i) => isHighSeverity(i.severityLevel));
  const rest = items.filter((i) => !isHighSeverity(i.severityLevel));
  const matched = highs.filter((item) => {
    const a = firstTokenCore(item.drugA || "");
    const b = firstTokenCore(item.drugB || "");
    const desc = (item.description || "").toLowerCase();
    const dlow = display.toLowerCase();
    if (a && (a === core || a.startsWith(core) || core.startsWith(a))) return true;
    if (b && (b === core || b.startsWith(core) || core.startsWith(b))) return true;
    if (desc.includes(dlow) || desc.includes(core)) return true;
    return false;
  });
  return matched.length > 0 ? [...matched, ...rest] : items;
}

/**
 * Scoped fallback for the prescription summary card. A render exception inside the
 * summary/edit UI (e.g. a malformed drugPopulate payload) previously tore down the whole
 * app via the single top-level ErrorBoundary in src/index.tsx — now it's contained to
 * just this card, with a retry action, so the rest of the conversation (chat history,
 * pharmacy pickers, etc) stays usable.
 */
function PrescriptionSummaryErrorFallback({
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: "1px solid #f5c2c7",
        borderRadius: "8px",
        bgcolor: "#fff5f5",
      }}
    >
      <Typography variant="body2" sx={{ color: "#842029", mb: 1 }}>
        Something went wrong displaying this prescription summary.
      </Typography>
      <Button size="small" variant="outlined" onClick={resetErrorBoundary}>
        Retry
      </Button>
    </Box>
  );
}

interface AIResponseBoxProps {
  aiResponse: string;
  isLoading: boolean;
  /** Accumulated WS `type: "status"` messages while the turn is in flight. */
  statusMessages?: string[];
  isLatest?: boolean;
  conversationId: number;
  showPharmacyList?: boolean;
  prescriptions?: any[];
  preferredPharmacy?: string;
  primaryColor: string;
  feedback?: "up" | "down" | null;
  onPharmacySelect?: (conversationId: number, pharmacyName: string) => void;
  onFeedback?: (conversationId: number, feedbackType: "up" | "down") => void;
  suggestions?: (string | any)[];
  suggestionGroups?: { medication_name: string; options: string[] }[];
  onSuggestionSelect?: (suggestionText: string, medicationName?: string) => void;
  /** Submits a chat message that triggers the send2Pharmacy flow (Send / Send all on the summary). */
  onSendPrescription?: (message: string) => void;
  /** True while a previous turn is still in flight; disables Send/Send all on the
   *  summary card so a double-click can't submit the same prescription twice. */
  sendDisabled?: boolean;
  selectedMedicationPicks?: Record<string, string>;
  pharmacyLookup?: (string | any)[];
  /** Why the pharmacy list isn't a plain local name match — see ConversationItem field doc. */
  pharmacySearchFallbackMode?: string | null;
  pharmacySuggestionGroups?: ConversationItem["pharmacySuggestionGroups"];
  selectedPharmacyPicks?: Record<string, string>;
  onPharmacyLookupSelect?: (
    pharmacyName: string,
    pharmacyId?: string,
    medicationName?: string
  ) => void;
  /** PBM/benefit-source picker rows (`pbm_suggestions_data`). */
  pbmLookup?: (string | any)[];
  onPbmSelect?: (displayMemberUniqueId: string, memberUniqueId?: string) => void;
  /** Doctor's favourite (saved) drugs picker rows (`favourite_drugs_data`). */
  favouriteDrugs?: (string | any)[];
  onFavouriteDrugSelect?: (drug: any) => void;
  drugInteractionResponse?: DrugInteractionResponse;
  /** On-demand patient allergy summary; renders the AllergySummaryCard when present. */
  allergyResponse?: AllergyResponse;
  currentState?: string;
  mfaPhase?: string;
  structuredMedicationData?: any[];
  /** Multi-intent envelope from orchestration (secondary intents only shown in UI when length > 1) */
  intents?: (IntentRecord | string)[];
  primaryIntent?: string;
  /** When set (including `[]`), drives summary / interactions / pharmacy surfaces from orchestration */
  uiBlocks?: string[];
  /** Structured in-chat nav control (`app_tab_link`); allowlisted paths only */
  appTabLink?: { path: string; label: string };
  /** All tab links for compound asks (`app_tab_links`) */
  appTabLinks?: { path: string; label: string }[];
}

const AIResponseBox: React.FC<AIResponseBoxProps> = ({
  aiResponse,
  isLoading,
  statusMessages,
  isLatest = true,
  conversationId,
  showPharmacyList = false,
  prescriptions = [],
  preferredPharmacy = "",
  primaryColor,
  feedback,
  onPharmacySelect,
  onFeedback,
  suggestions = [],
  suggestionGroups,
  onSuggestionSelect,
  onSendPrescription,
  sendDisabled = false,
  selectedMedicationPicks,
  pharmacyLookup = [],
  pharmacySearchFallbackMode,
  pharmacySuggestionGroups,
  selectedPharmacyPicks,
  onPharmacyLookupSelect,
  pbmLookup = [],
  onPbmSelect,
  favouriteDrugs = [],
  onFavouriteDrugSelect,
  drugInteractionResponse,
  allergyResponse,
  currentState,
  mfaPhase,
  structuredMedicationData,
  intents,
  primaryIntent: primaryIntentProp,
  uiBlocks,
  appTabLink,
  appTabLinks,
}) => {
  const navigate = useNavigate();
  const diIntentLowerEarly = (drugInteractionResponse?.intent ?? "").toString().toLowerCase();

  const handlePharmacyClick = (pharmacyName: string) => {
    if (onPharmacySelect) {
      onPharmacySelect(conversationId, pharmacyName);
    }
  };

  const pharmaciesForList = useMemo(
    () => getPharmaciesFromPharmacyLookup(pharmacyLookup, preferredPharmacy),
    [pharmacyLookup, preferredPharmacy]
  );

  const groupedPharmacyLookup = useMemo(
    () =>
      pharmacySuggestionGroups?.length
        ? pharmacySuggestionGroups
        : pharmacySuggestionGroupsFromPayload(undefined, structuredMedicationData),
    [pharmacySuggestionGroups, structuredMedicationData]
  );

  const intentNameSet = useMemo(() => {
    if (!intents?.length) return new Set<string>();
    return new Set(
      intents
        .map((r) => {
          if (typeof r === "string") return r.trim().toLowerCase();
          return (r as IntentRecord).intent?.trim().toLowerCase() || "";
        })
        .filter(Boolean)
    );
  }, [intents]);

  /** Orchestration multi-intent: medication + pharmacy suggestions in one result */
  const parallelMedAndPharmacy =
    intentNameSet.has("pharmacy_suggestions") &&
    Array.isArray(pharmacyLookup) &&
    pharmacyLookup.length > 0;

  const deferPharmacyForMedicationStep = shouldDeferPharmacyForMedicationSuggestions(
    structuredMedicationData,
    suggestions,
    { allowSimultaneousPharmacyPicker: parallelMedAndPharmacy }
  );

  /** Secondary intents shown as a caption; omit DI intents (interaction card covers them). */
  const secondaryIntentLine = useMemo(() => {
    if (!intents || intents.length < 2) return "";
    const names = intents.map((r) =>
      typeof r === "string" ? r : (r as IntentRecord).intent || ""
    );
    const prim = (primaryIntentProp || names[0] || "").toLowerCase();
    const redundant = new Set(["drug_interactions", "view_interactions"]);
    if (prim === INTENT_HIGH_SEVERITY || diIntentLowerEarly === INTENT_HIGH_SEVERITY) {
      redundant.add("therapeutic_alternates");
    }
    const labels = names
      .filter((n) => n && n.toLowerCase() !== prim && !redundant.has(n.trim().toLowerCase()))
      .map((n) => n.replace(/_/g, " "));
    return labels.length > 0 ? labels.join(", ") : "";
  }, [intents, primaryIntentProp, diIntentLowerEarly]);

  const allInteractions = useMemo(
    () => drugInteractionResponseToInteractionItems(drugInteractionResponse),
    [drugInteractionResponse]
  );

  const highSeverityPrimaryMedName =
    drugInteractionResponse?.high_severity_rtf_primary_medication_name?.trim();

  const multiRxDraft = isMultiRxPrescriptionDraft(
    structuredMedicationData,
    prescriptions
  );

  const interactionsForCard = useMemo(() => {
    const intent = (drugInteractionResponse?.intent ?? "").toString().toLowerCase();
    if (
      intent !== INTENT_HIGH_SEVERITY ||
      !highSeverityPrimaryMedName ||
      multiRxDraft
    ) {
      return allInteractions;
    }
    return filterHighInteractionsForPrimaryMedication(allInteractions, highSeverityPrimaryMedName);
  }, [
    allInteractions,
    drugInteractionResponse?.intent,
    highSeverityPrimaryMedName,
    multiRxDraft,
  ]);

  const highSeverityReviewResolved = Boolean(
    drugInteractionResponse?.high_severity_prescription_review_resolved
  );

  function prescriptionMatchesPrimaryName(prescription: any, primary: string): boolean {
    const pr = primary.trim().toLowerCase();
    if (!pr) return false;
    const firstCore = (s: string) => {
      const t = String(s || "")
        .trim()
        .split(/\s+/)[0] ?? "";
      return t.replace(/[^a-zA-Z0-9-]/g, "").toLowerCase();
    };
    const pt = firstCore(pr);
    const candidates: string[] = [];
    const push = (v: unknown) => {
      if (v != null && String(v).trim()) candidates.push(String(v).trim().toLowerCase());
    };
    push(prescription?.medication_name);
    push(prescription?.title);
    push(prescription?.label);
    push(prescription?.name);
    if (prescription?.summary_snapshot && typeof prescription.summary_snapshot === "object") {
      push((prescription.summary_snapshot as { medication_name?: string }).medication_name);
    }
    return candidates.some((c) => {
      if (c === pr) return true;
      if (c.includes(pr) || pr.includes(c)) return true;
      return firstCore(c) === pt && pt.length > 2;
    });
  }

  const prescriptionsForSummary = useMemo(() => {
    const intentL = (drugInteractionResponse?.intent ?? "").toString().toLowerCase();
    return filterPrescriptionsForSummaryDisplay(prescriptions, {
      highSeverityIntent: intentL === INTENT_HIGH_SEVERITY,
      highSeverityPrimaryMedName,
      highSeverityReviewResolved,
      structuredMedicationData,
      prescriptionMatchesPrimaryName: prescriptionMatchesPrimaryName,
    });
  }, [
    prescriptions,
    drugInteractionResponse?.intent,
    highSeverityPrimaryMedName,
    highSeverityReviewResolved,
    structuredMedicationData,
  ]);

  const confirmedPrescriptionsDuringPicker = useMemo(
    () =>
      prescriptionsForLockedMedications(
        prescriptions,
        structuredMedicationData
      ),
    [prescriptions, structuredMedicationData]
  );

  // Normalize intent for case-insensitive comparison (API may send "High_Severity", etc.)
  const intentLower = (drugInteractionResponse?.intent ?? "").toString().toLowerCase();

  const apiCounts = drugInteractionResponse?.current_medication_only_counts;
  const apiCountsTotal = apiCounts
    ? (apiCounts.high ?? 0) + (apiCounts.moderate ?? 0) + (apiCounts.minor ?? 0)
    : 0;
  const hasApiCounts =
    apiCounts &&
    (apiCounts.high !== undefined || apiCounts.moderate !== undefined || apiCounts.minor !== undefined);
  const hideInteractionsForPharmacyStep =
    (currentState ?? "").toUpperCase() === "PHARMACY_SUGGESTIONS";

  const drivenByUiBlocks = uiBlocks !== undefined && uiBlocks !== null;
  const uiBlockSet = useMemo(() => {
    if (!drivenByUiBlocks) return null;
    return new Set((uiBlocks || []).map((b) => b.trim().toLowerCase()).filter(Boolean));
  }, [drivenByUiBlocks, uiBlocks]);

  const safeAppTabLinks = useMemo(() => {
    if (uiBlockSet && !uiBlockSet.has("app_tab_link")) return [];
    const fromList = normalizeAppTabLinks(
      appTabLinks && appTabLinks.length > 0 ? appTabLinks : appTabLink
    );
    return fromList.filter((link) => isAllowedAppTabPath(link.path));
  }, [uiBlockSet, appTabLinks, appTabLink]);
  const safeAppTabLink = safeAppTabLinks[0] ?? null;

  const showInteractionsSurface = uiBlockSet
    ? uiBlockSet.has("interactions")
    : !hideInteractionsForPharmacyStep;

  const hasDrugInteractionToShow =
    showInteractionsSurface &&
    drugInteractionResponse != null &&
    (allInteractions.length > 0 || apiCountsTotal > 0 || hasApiCounts);

  const highInteractionsCount = allInteractions.filter((item) => isHighSeverity(item.severityLevel)).length;
  const showHighSeverityScreen = intentLower === INTENT_HIGH_SEVERITY && highInteractionsCount > 0;

  const viewInteractions = intentLower === INTENT_VIEW_INTERACTIONS;
  const primaryIntentLower = (primaryIntentProp ?? "").toString().toLowerCase();
  const mfaPhaseLower = (mfaPhase ?? "").toString().toLowerCase();
  const showScheduledMfaLivePanel =
    primaryIntentLower === "verification_required" ||
    mfaPhaseLower === "need_mfa" ||
    mfaPhaseLower === "waiting_code" ||
    mfaPhaseLower === "retry";

  // When true: show only the high-severity drug interaction card (hide AI text, suggestions, pharmacy, medication summary)
  const isHighSeverityIntent = intentLower === INTENT_HIGH_SEVERITY;
  /** Cached DI can keep ``intent: high_severity`` while the user is in pharmacy search — still show pharmacy picker. */
  const pharmacyRoutingFlow =
    (currentState ?? "").toUpperCase() === "PHARMACY_SUGGESTIONS" ||
    primaryIntentLower === "pharmacy_suggestions" ||
    primaryIntentLower === "pharmacy_lookup" ||
    primaryIntentLower === "get_favourite_pharmacy";
  const suppressPharmacyChromeForDiHighSeverity =
    isHighSeverityIntent && !pharmacyRoutingFlow;

  /** Hide the medication summary while any picker/list needs a choice (server may still send ui_blocks summary). */
  const hidePrescriptionSummaryDuringSuggestions =
    deferPharmacyForMedicationStep ||
    (!!onSuggestionSelect &&
      Array.isArray(suggestions) &&
      suggestions.length > 0) ||
    (!!onPharmacyLookupSelect &&
      Array.isArray(pharmacyLookup) &&
      pharmacyLookup.length > 0) ||
    (showPharmacyList && pharmaciesForList.length > 0) ||
    ["SELECT_SUGGESTION", "PHARMACY_SUGGESTIONS"].includes(
      (currentState ?? "").toUpperCase()
    ) ||
    primaryIntentLower === "pharmacy_suggestions" ||
    intentNameSet.has("pharmacy_suggestions");

  const summaryPrescriptions: typeof prescriptions =
    hidePrescriptionSummaryDuringSuggestions &&
      confirmedPrescriptionsDuringPicker.length > 0
      ? (confirmedPrescriptionsDuringPicker as typeof prescriptions)
      : prescriptionsForSummary;

  const stateUpper = (currentState ?? "").toUpperCase();
  const showSummarySurface =
    // Don't show the prescription summary card while the MFA/verification step is up —
    // the identity-verification panel is the only thing the doctor should act on there.
    !showScheduledMfaLivePanel &&
    (!hidePrescriptionSummaryDuringSuggestions ||
      confirmedPrescriptionsDuringPicker.length > 0) &&
    (uiBlockSet
      ? uiBlockSet.has("summary") && summaryPrescriptions.length > 0
      : !viewInteractions &&
      summaryPrescriptions.length > 0 &&
      (stateUpper === "READY_TO_CONFIRM" ||
        stateUpper === "SELECT_SUGGESTION" ||
        confirmedPrescriptionsDuringPicker.length > 0));

  const showPharmacyPickerSurface =
    !!onPharmacyLookupSelect &&
    !suppressPharmacyChromeForDiHighSeverity &&
    !deferPharmacyForMedicationStep &&
    Array.isArray(pharmacyLookup) &&
    pharmacyLookup.length > 0 &&
    (uiBlockSet ? uiBlockSet.has("pharmacy_picker") : true);

  const showPbmPickerSurface =
    !!onPbmSelect &&
    Array.isArray(pbmLookup) &&
    pbmLookup.length > 0 &&
    (uiBlockSet ? uiBlockSet.has("pbm_picker") : true);

  const showFavouriteDrugsSurface =
    !!onFavouriteDrugSelect &&
    Array.isArray(favouriteDrugs) &&
    favouriteDrugs.length > 0 &&
    (uiBlockSet ? uiBlockSet.has("favourite_drugs_picker") : true);

  // Show content when we have AI response text and/or drug interaction / allergy response (don't hide previous response)
  const hasContentToShow =
    !isLoading &&
    (aiResponse || drugInteractionResponse || allergyResponse || safeAppTabLinks.length > 0);

  return (
    <Box
      style={{ width: "100%" }}
      display="flex"
      alignItems="flex-start"
      gap={1}
    >
      <ChatbotIcon color={primaryColor} />
      <Box
        sx={{
          width: "100%",
          flex: 1,
          backgroundColor: "#ffffff",
          padding: "16px 60px 16px 20px",
          borderRadius: "12px",
          border: "1px solid #e9ecef",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          position: "relative",
        }}
      >
        {/* Only show copy icon when not loading and there's response text to copy */}
        {!isLoading && aiResponse && (
          <CopyButton text={aiResponse} primaryColor={primaryColor} />
        )}
        {isLoading ? (
          <AIPrescriptionFetchingProcess statusMessages={statusMessages} />
        ) : hasContentToShow ? (
          <motion.div
            key={`response-${conversationId}`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            style={{ width: "100%" }}
          >
            <Box sx={{ width: "100%" }}>
              {/* Show AI response text when present (e.g. "After reviewing..." for high_severity) */}
              {aiResponse && !showScheduledMfaLivePanel && (
                <Typography
                  variant="body1"
                  component="div"
                  sx={{
                    lineHeight: 1.6,
                    color: "#212529",
                    fontWeight: 400,
                  }}
                >
                  {capitalizeFirstLetter(aiResponse)}
                </Typography>
              )}
              {safeAppTabLinks.length > 0 && (
                <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {safeAppTabLinks.map((link) => (
                    <Button
                      key={link.path}
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(link.path)}
                      sx={{
                        textTransform: "none",
                        borderColor: primaryColor || "#0d6efd",
                        color: primaryColor || "#0d6efd",
                      }}
                    >
                      Open {link.label}
                    </Button>
                  ))}
                </Box>
              )}
              {showScheduledMfaLivePanel && (
                <ScheduledMfaLivePanel phase={mfaPhaseLower || undefined} active={isLatest} />
              )}

              {secondaryIntentLine && (
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ mt: 0.75, color: "#6c757d", fontStyle: "italic" }}
                >
                  Also detected: {secondaryIntentLine}
                </Typography>
              )}

              {/* Suggestions - Display below response text (hidden for high_severity).
                  Rendered (not hidden) on past turns too, but `disabled` there -- an older
                  turn's chip list must stay visible for readable history, but clicking one
                  must not re-fire that turn's action against the conversation's current
                  (now different) state. */}
              {((suggestionGroups && suggestionGroups.length > 0) ||
                (suggestions && suggestions.length > 0)) &&
                onSuggestionSelect &&
                !suppressPharmacyChromeForDiHighSeverity && (
                  <Suggestions
                    suggestions={suggestions}
                    suggestionGroups={suggestionGroups}
                    selectedPicksByMedication={picksForConversation(
                      selectedMedicationPicks || {},
                      conversationId
                    )}
                    primaryColor={primaryColor}
                    onSuggestionSelect={onSuggestionSelect}
                    pendingOtherPicker={groupedPharmacyLookup.length > 0}
                    disabled={!isLatest}
                  />
                )}

              {allergyResponse != null && (
                <Box sx={{ mt: 2 }}>
                  <AllergySummaryCard allergyResponse={allergyResponse} />
                </Box>
              )}

              {drivenByUiBlocks ? (
                <>
                  {hasDrugInteractionToShow && (
                    <Box sx={{ mt: 2 }}>
                      <InteractionsSummaryCard
                        allInteractions={interactionsForCard}
                        intent={intentLower}
                        apiCounts={apiCounts}
                      />
                    </Box>
                  )}
                  {showSummarySurface && (
                    <Box sx={{ mt: hasDrugInteractionToShow ? 2 : 0 }}>
                      <ErrorBoundary FallbackComponent={PrescriptionSummaryErrorFallback}>
                        <PrescriptionSummary
                          prescriptions={summaryPrescriptions}
                          conversationId={conversationId}
                          preferredPharmacy={preferredPharmacy}
                          onSendPrescription={onSendPrescription}
                          disabled={sendDisabled}
                        />
                      </ErrorBoundary>
                    </Box>
                  )}
                  {showPharmacyPickerSurface && (
                    <PharmacyLookup
                      pharmacies={pharmacyLookup}
                      fallbackMode={pharmacySearchFallbackMode}
                      pharmacyGroups={groupedPharmacyLookup}
                      selectedPicksByMedication={picksForConversation(
                        selectedPharmacyPicks || {},
                        conversationId
                      )}
                      selectedPharmacyName={preferredPharmacy}
                      primaryColor={primaryColor}
                      onPharmacySelect={onPharmacyLookupSelect}
                      pendingOtherPicker={(suggestionGroups?.length ?? 0) > 0}
                      disabled={!isLatest}
                    />
                  )}
                  {showPbmPickerSurface && onPbmSelect && (
                    <PbmLookup
                      plans={pbmLookup}
                      primaryColor={primaryColor}
                      onPbmSelect={onPbmSelect}
                      disabled={!isLatest}
                    />
                  )}
                  {showFavouriteDrugsSurface && onFavouriteDrugSelect && (
                    <FavouriteDrugsLookup
                      drugs={favouriteDrugs}
                      primaryColor={primaryColor}
                      onDrugSelect={onFavouriteDrugSelect}
                      disabled={!isLatest}
                    />
                  )}
                  {showPharmacyList &&
                    pharmaciesForList.length > 0 &&
                    !suppressPharmacyChromeForDiHighSeverity &&
                    !deferPharmacyForMedicationStep && (
                      <PharmacyList
                        pharmacies={pharmaciesForList}
                        primaryColor={primaryColor}
                        onPharmacySelect={handlePharmacyClick}
                        disabled={!isLatest}
                      />
                    )}
                </>
              ) : (
                <>
                  {pharmacyLookup &&
                    pharmacyLookup.length > 0 &&
                    onPharmacyLookupSelect &&
                    !suppressPharmacyChromeForDiHighSeverity &&
                    !deferPharmacyForMedicationStep && (
                      <PharmacyLookup
                        pharmacies={pharmacyLookup}
                        fallbackMode={pharmacySearchFallbackMode}
                        pharmacyGroups={groupedPharmacyLookup}
                        selectedPicksByMedication={picksForConversation(
                          selectedPharmacyPicks || {},
                          conversationId
                        )}
                        selectedPharmacyName={preferredPharmacy}
                        primaryColor={primaryColor}
                        onPharmacySelect={onPharmacyLookupSelect}
                        pendingOtherPicker={(suggestionGroups?.length ?? 0) > 0}
                        disabled={!isLatest}
                      />
                    )}

                  {pbmLookup && pbmLookup.length > 0 && onPbmSelect && (
                    <PbmLookup
                      plans={pbmLookup}
                      primaryColor={primaryColor}
                      onPbmSelect={onPbmSelect}
                      disabled={!isLatest}
                    />
                  )}

                  {favouriteDrugs && favouriteDrugs.length > 0 && onFavouriteDrugSelect && (
                    <FavouriteDrugsLookup
                      drugs={favouriteDrugs}
                      primaryColor={primaryColor}
                      onDrugSelect={onFavouriteDrugSelect}
                      disabled={!isLatest}
                    />
                  )}

                  {showPharmacyList &&
                    pharmaciesForList.length > 0 &&
                    !suppressPharmacyChromeForDiHighSeverity &&
                    !deferPharmacyForMedicationStep && (
                      <PharmacyList
                        pharmacies={pharmaciesForList}
                        primaryColor={primaryColor}
                        onPharmacySelect={handlePharmacyClick}
                        disabled={!isLatest}
                      />
                    )}

                  {hasDrugInteractionToShow && (
                    <Box sx={{ mt: 2 }}>
                      <InteractionsSummaryCard
                        allInteractions={interactionsForCard}
                        intent={intentLower}
                        apiCounts={apiCounts}
                      />
                    </Box>
                  )}
                  {showSummarySurface && (
                    <Box sx={{ mt: hasDrugInteractionToShow ? 2 : 0 }}>
                      <ErrorBoundary FallbackComponent={PrescriptionSummaryErrorFallback}>
                        <PrescriptionSummary
                          prescriptions={summaryPrescriptions}
                          conversationId={conversationId}
                          preferredPharmacy={preferredPharmacy}
                          onSendPrescription={onSendPrescription}
                          disabled={sendDisabled}
                        />
                      </ErrorBoundary>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </motion.div>
        ) : null}

        {/* Feedback buttons at bottom right - only show when not loading and there's content */}
        {!isLoading && hasContentToShow && onFeedback && (
          <FeedbackButtons
            conversationId={conversationId}
            currentFeedback={feedback}
            onFeedback={onFeedback}
            primaryColor={primaryColor}
          />
        )}
      </Box>
    </Box>
  );
};

export default AIResponseBox;

